import {
  EnrollmentCallGapsResponse,
  type EnrollmentCallGapsParams,
  type EnrollmentCallGapDay,
} from "@t3tools/contracts";
import { queryOptions } from "@tanstack/react-query";
import { Schema } from "effect";
import { dashboardsQueryKeys, fetchEnrollmentJson } from "./dashboardsReactQuery";

export function previousDate(iso: string): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
}

export function lastFiveWorkingDays(today: string) {
  const dates: string[] = [];
  for (let date = previousDate(today); dates.length < 5; date = previousDate(date)) {
    const day = new Date(`${date}T00:00:00Z`).getUTCDay();
    if (day !== 0 && day !== 6) dates.unshift(date);
  }
  return { start: dates[0]!, end: dates.at(-1)!, dates };
}

export function callGapDuration(minutes: number): string {
  const rounded = Math.round(minutes);
  return `${Math.floor(rounded / 60)}h ${String(rounded % 60).padStart(2, "0")}m`;
}

export function callGapClock(minute: number): string {
  const totalSeconds = Math.round(minute * 60);
  const hour = Math.floor(totalSeconds / 3600);
  return `${hour % 12 || 12}:${String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0")}:${String(totalSeconds % 60).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}

export function callGapTotals(days: readonly EnrollmentCallGapDay[]) {
  return days.reduce(
    (sum, day) => ({
      calls: sum.calls + day.calls,
      callMinutes: sum.callMinutes + day.callMinutes,
      nonCallMinutes: sum.nonCallMinutes + day.nonCallMinutes,
      longGapMinutes: sum.longGapMinutes + day.longGapMinutes,
      longGapCount: sum.longGapCount + day.longGapCount,
    }),
    { calls: 0, callMinutes: 0, nonCallMinutes: 0, longGapMinutes: 0, longGapCount: 0 },
  );
}

export function enrollmentCallGapsQueryOptions(params: EnrollmentCallGapsParams) {
  return queryOptions({
    queryKey: [...dashboardsQueryKeys.all, "enrollment", "es-call-gaps", params] as const,
    staleTime: 60_000,
    queryFn: async () => {
      const search = new URLSearchParams({
        start: params.start,
        end: params.end,
        weekdaysOnly: String(params.weekdaysOnly ?? false),
      });
      if (params.specialist) search.set("specialist", params.specialist);
      const body = await fetchEnrollmentJson(
        `/api/v1/dashboards/enrollment/es-call-gaps?${search}`,
        "Unable to load Dialpad call gaps.",
      );
      let data: EnrollmentCallGapsResponse;
      try {
        data = Schema.decodeUnknownSync(EnrollmentCallGapsResponse)(body);
      } catch {
        throw new Error("Dialpad call gaps response was malformed.");
      }
      if (
        data.start !== params.start ||
        data.end !== params.end ||
        data.specialist !== (params.specialist ?? null) ||
        data.weekdaysOnly !== (params.weekdaysOnly ?? false)
      ) {
        throw new Error("Dialpad call gaps response did not match the selected filters.");
      }
      return data;
    },
  });
}
